import { action } from "@ember/object"
import { inject as service } from "@ember/service"
import Component from "@glimmer/component"
import type { Course } from "@shared/models/course"
import type {
  Planbook,
  PlanbookDateAnnotationDateRange,
  PlanbookDateAnnotationSingle,
} from "@shared/models/planbook"
import type { RotationCalendar } from "@shared/models/rotation-calendar"
import { handleError } from "cc-frontend/error-handling"
import ENV from "cc-frontend/config/environment"
import { PLANBOOK_DATE_ANNOTATION_DELETE } from "cc-frontend/lib/actions/planbook/date/PLANBOOK_DATE_ANNOTATION_DELETE"
import type Dispatcher from "cc-frontend/services/dispatcher"
import type EmberStore from "cc-frontend/services/ember-store"
import type ModalManager from "cc-frontend/services/modal-manager"
import type PlanbookEditor from "cc-frontend/services/planbook-editor"
import type { Args } from "../modals/edit-annotation"

interface Sig {
  Args: {
    planbook: Planbook
    rotationCalendar: RotationCalendar
    dateString: string
    annotation: PlanbookDateAnnotationSingle | PlanbookDateAnnotationDateRange
    closeMenu: () => void
  }
}

export default class PlanbookDateAnnotationMenu extends Component<Sig> {
  // Services
  @service("ember-store") store!: EmberStore
  @service dispatcher!: Dispatcher
  @service modalManager!: ModalManager
  @service planbookEditor!: PlanbookEditor

  // Actions
  @action
  async edit() {
    this.args.closeMenu()
    let startDate
    let endDate
    let dateIs
    let datesAreEditable
    let isRange = this.args.annotation.type === "date-annotation-planbook-custom-range"
    if (this.args.annotation.type === "date-annotation-planbook-custom-range") {
      startDate = this.args.annotation.attributes.startDate
      endDate = this.args.annotation.attributes.endDate
      dateIs = "FLEXIBLE_RANGE"
      datesAreEditable = true
    } else if (this.args.annotation.type === "date-annotation-planbook-custom-single") {
      startDate = this.args.annotation.attributes.dateString
      endDate = this.args.annotation.attributes.dateString
      dateIs = "SINGLE_DATE"
      datesAreEditable = true
    }

    const courses = await Promise.all(
      this.args.planbook.relationships.courses.data
        .filter((relation) => !!relation.id)
        .map((relation) => this.store.find("course", relation.id) as Promise<Course>)
    )
    const models = {
      planbook: this.args.planbook,
      rotationCalendar: this.args.rotationCalendar,
      courses,
    }

    this.modalManager.openModal("planner/modals/edit-annotation", {
      allowRangeToggle: false,
      allowNoClassToggle: true,
      dateIs: dateIs,
      datesAreEditable: datesAreEditable,
      description: this.args.annotation.attributes.description,
      endDate: endDate,
      isRange: isRange,
      classIsOff: this.args.annotation.attributes.isOff,
      modalTitle: "Edit Special Event",
      startDate: startDate,
      submitButtonText: "Update",
      title: this.args.annotation.attributes.title,
      submitAction: ({ title, description, endDate, startDate, isOff }) => {
        this.planbookEditor.updateAnnotation({
          oldAnnotation: this.args.annotation,
          title: title,
          description: description,
          startDate: startDate,
          endDate: endDate,
          isOff: Boolean(isOff),
          models,
          bumpDeleteOrNothing: isOff ? "BUMP" : "NOTHING",
        })
      },
    } as Args)
  }

  @action
  delete() {
    this.args.closeMenu()
    let deleteIt = () => {
      this.dispatcher.dispatchAction(PLANBOOK_DATE_ANNOTATION_DELETE, {
        annotation: this.args.annotation,
        planbook: this.args.planbook,
        calledFrom: "planbook-date-annotation-menu",
      })
    }
    // Because we can't test swal until we:
    // 1. upgrade the lib to the ember-sweetalert library
    // 2. change all our uses we are stuck doing this terrible thing.
    // Future self, I'm sorry.
    if (ENV.environment === "test") {
      deleteIt()
    } else {
      window
        .swal({
          title: "Remove Special Event?",
          text: "This will also remove the yellow note.",
          type: "warning",
          showCancelButton: true,
        })
        .then(deleteIt)
        .catch((error) => {
          if (error instanceof Error) handleError(error)
        })
    }
  }
}

declare module "@glint/environment-ember-loose/registry" {
  export default interface Registry {
    "Planner::Menus::DateAnnotationMenu": typeof PlanbookDateAnnotationMenu
  }
}
